import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography, Link, Paper } from '@mui/material';
import { DIALOG_TYPE } from '../../../../../../constants';
import {
  CalendarClockIcons,
  CircleCheckIcons,
  LinkIcons,
  MegaPhoneIcons,
} from '../../../../../../icons/icons';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import {
  Button,
  ContextMenu,
  DataTable,
  Dialog,
  FilterSearch,
  FloatingLabelInput,
  MainLayout,
} from '../../../../../../ui';
import { TrashIcon } from '../../../../../../ui/icons/icons';
import {
  getInterviewRound,
  interviewRoundResults,
} from '../../../../../../utils/api_service/SmartInterview';
import { ICandidate } from '../../../../../../utils/types/JobOpenings';
import { IInterviewResult } from '../Results/Hooks';
import { RecordInterviewDialog } from './Components';
import { InviteCandidates } from './Containers';
import { PanelistNotesDialog } from './Containers/Notes';
import { getColumns } from './Functions';
import { useCandidates } from './Hooks';

export interface ICandidatesProps {
  interviewRoundID: string;
  jobOpeningId: string;
  allRounds: Array<{
    id: string;
    title: string;
  }>;
  currentRoundIndex: number;
}

export const Candidates: React.FC<ICandidatesProps> = ({
  interviewRoundID,
  allRounds,
  currentRoundIndex,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;
  const {
    handleSearch,
    handleApplyFilters,
    onPageChange,
    totalPages,
    pageNumber,
    interviewApplicants,
    isLoading,
    totalRecords,
    handleRecordingSave,
    openInvite,
    openMenu,
    closeMenu,
    anchorEl,
    isOpenInvite,
    closeInvite,
    onInvitesSent,
    selectedRow,
    menuContainerRef,
    makeMenuItemsFor,
    isDeleteOpen,
    handleConfirmDelete,
    setDeleteOpen,
    setSelectedRow,
    isUpdateOpen,
    handleApplyUpdate,
    setUpdateOpen,
    isVirtualType,
    meetingLink,
    setMeetingLink,
    isConfirmOpen,
    inPersonAddress,
    setInPersonAddress,
    handleConfirmInterview,
    setConfirmOpen,
    isPanelistNotesOpen,
    handleClosePanelistNotes,
    onOpenPanelistNotes,
    recordingRowId,
    openRecordClick,
    closeRecordClick,
    isRecordingDialogOpen,
  } = useCandidates({ interviewRoundID });
  const [isNotFirstRound, setIsNotFirstRound] = useState(false);

  // Fetch round details when component mounts or interviewRoundID changes
  useEffect(() => {
    const fetchRoundDetails = async () => {
      if (!interviewRoundID) return;

      try {
        const resp = await getInterviewRound(interviewRoundID);
        if (!resp.ok) {
          throw new Error(`Failed to fetch round details: ${resp.statusText}`);
        }

        const data = await resp.json();
        const isNotFirst =
          data.title?.toLowerCase().includes('round') &&
          !data.title?.toLowerCase().includes('round 1');
        setIsNotFirstRound(isNotFirst);
      } catch {
        setIsNotFirstRound(false);
      }
    };

    fetchRoundDetails();
  }, [interviewRoundID]);

  // Invite Drawer
  const [results, setResults] = useState<IInterviewResult[]>([]);

  // Fetch results when interviewRoundID changes
  React.useEffect(() => {
    const fetchResults = async () => {
      if (!interviewRoundID || currentRoundIndex < 0) return;

      try {
        if (currentRoundIndex > 0) {
          const previousRound = allRounds[currentRoundIndex - 1];
          const resp = await interviewRoundResults(previousRound.id);

          if (!resp.ok) {
            throw new Error(`Failed to fetch results: ${resp.statusText}`);
          }

          const data = await resp.json();
          setResults(data.results || []);
        } else {
          setResults([]);
        }
      } catch {
        setResults([]);
      }
    };

    if (interviewRoundID) {
      fetchResults();
    }
  }, [interviewRoundID, currentRoundIndex, allRounds]);

  return (
    <>
      <MainLayout>
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            flexDirection: { xs: 'column', xl: 'row' },
            gap: 1.5,
            alignItems: { xl: 'center' },
            justifyContent: { xl: 'space-between' },
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <FilterSearch
              onSearch={handleSearch}
              onApplyFilters={handleApplyFilters}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
              alignItems: { sm: 'center' },
              justifyContent: { sm: 'space-between', xl: 'flex-end' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                minWidth: 0,
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1.5,
                mr: { sm: 1 },
              }}
            >
              {[
                {
                  color: 'linear-gradient(to right, #6366f1, #4f46e5)',
                  label: t(SI.candidates.status.invited),
                },
                {
                  color: 'linear-gradient(to right, #8b5cf6, #7c3aed)',
                  label: t(SI.candidates.status.scheduled),
                },
                {
                  color: 'linear-gradient(to right, #3b82f6, #2563eb)',
                  label: t(SI.candidates.status.confirmed),
                },
                {
                  color: 'linear-gradient(to right, #10b981, #059669)',
                  label: t(SI.candidates.status.live),
                },
                {
                  color: 'linear-gradient(to right, #22c55e, #16a34a)',
                  label: t(SI.candidates.status.finished),
                },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                >
                  <Box
                    sx={{
                      height: 8,
                      width: 8,
                      borderRadius: 0.5,
                      background: item.color,
                      boxShadow: 1,
                    }}
                  />
                  <Typography
                    variant='caption'
                    sx={{
                      fontWeight: 500,
                      color: 'text.secondary',
                      fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Button
              label={t(SI.candidates.inviteButton)}
              variant='primary'
              onClick={openInvite}
              sx={{ minHeight: 36, width: { xs: '100%', sm: 'auto' }, px: 1.5, py: 0.75, fontSize: '0.875rem' }}
            />
          </Box>
        </Box>
        <DataTable
          isLoading={isLoading}
          columns={getColumns({
            handleOpenMenu: openMenu,
            handleCloseMenu: closeMenu,
            anchorEl,
            onRecordInterview: openRecordClick,
            onOpenPanelistNotes: onOpenPanelistNotes,
            recordingRowId: recordingRowId,
            t,
          })}
          data={interviewApplicants}
          rowsPerPage={10}
          currentPage={pageNumber}
          onPageChange={onPageChange}
          totalPages={totalPages}
          totalRecords={totalRecords}
        />
      </MainLayout>
      <InviteCandidates
        interviewRoundID={interviewRoundID}
        isOpen={isOpenInvite}
        onCloseDrawer={closeInvite}
        onInvitesSent={onInvitesSent}
        results={results}
        isNotFirstRound={isNotFirstRound}
      />
      {anchorEl && selectedRow && (
        <Box ref={menuContainerRef}>
          <ContextMenu<ICandidate>
            anchorEl={anchorEl}
            onClose={closeMenu}
            row={selectedRow}
            menuItems={makeMenuItemsFor(selectedRow)}
          />
        </Box>
      )}
      {/* Delete Candidate */}
      <Dialog
        isOpen={isDeleteOpen}
        title={t(SI.candidates.deleteDialog.title)}
        handleApply={() => selectedRow && handleConfirmDelete(selectedRow)}
        cancelButtonLable={t(SI.candidates.deleteDialog.cancel)}
        confirmButtonLable={t(SI.candidates.deleteDialog.confirm)}
        dialogType={DIALOG_TYPE.ERROR}
        onClose={() => {
          setDeleteOpen(false);
          setSelectedRow(null);
        }}
      >
        <Stack spacing={2} alignItems='center' sx={{ p: 2 }}>
          <TrashIcon size={48} color='#dc2626' />
          <Typography variant='h6' sx={{ textAlign: 'center', color: 'text.primary' }}>
            Are you sure you want to remove{' '}
            <Typography component='span' variant='h6' sx={{ fontWeight: 600 }}>
              {selectedRow?.firstName} {selectedRow?.lastName}
            </Typography>{' '}
            from this interview?
          </Typography>
          <Typography variant='body2' sx={{ textAlign: 'center', color: 'text.secondary' }}>
            {t(SI.candidates.deleteDialog.warning)}
          </Typography>
        </Stack>
      </Dialog>
      {/* Update Interview Details */}
      <Dialog
        isOpen={isUpdateOpen}
        title={t(SI.candidates.updateDialog.title)}
        handleApply={() => selectedRow && handleApplyUpdate(selectedRow)}
        cancelButtonLable={t(SI.candidates.updateDialog.cancel)}
        confirmButtonLable={t(SI.candidates.updateDialog.save)}
        dialogType={DIALOG_TYPE.INFO}
        onClose={() => {
          setUpdateOpen(false);
          setSelectedRow(null);
        }}
        disabled={isVirtualType && meetingLink.trim() === ''}
      >
        <Stack spacing={2} sx={{ p: 1 }}>
          <Typography variant='body1'>
            {isVirtualType
              ? t(SI.candidates.updateDialog.virtual)
              : t(SI.candidates.updateDialog.inPerson)}{' '}
            Interview
          </Typography>
          {isVirtualType ? (
            <FloatingLabelInput
              label={t(SI.candidates.updateDialog.meetingLink)}
              value={meetingLink}
              onChange={(e) => setMeetingLink(e.target.value)}
              placeholder='https://zoom.us/…'
            />
          ) : (
            <FloatingLabelInput
              label={t(SI.candidates.updateDialog.physicalAddress)}
              value={inPersonAddress}
              onChange={(e) => setInPersonAddress(e.target.value)}
              placeholder='123 Main St, City'
            />
          )}
        </Stack>
      </Dialog>
      {/* Confirm Interview */}
      <Dialog
        isOpen={isConfirmOpen}
        title={t(SI.candidates.confirmDialog.title)}
        handleApply={() => selectedRow && handleConfirmInterview(selectedRow)}
        cancelButtonLable={t(SI.candidates.confirmDialog.cancel)}
        confirmButtonLable={t(SI.candidates.confirmDialog.confirm)}
        dialogType={DIALOG_TYPE.INFO}
        onClose={() => {
          setConfirmOpen(false);
          setSelectedRow(null);
        }}
      >
        {selectedRow && (
          <Stack spacing={3} sx={{ p: 2 }}>
            {/* Date / Time */}
            <Paper
              variant='outlined'
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                bgcolor: 'action.hover',
              }}
            >
              <CalendarClockIcons size={24} color='#047857' />
              <Box>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  {t(SI.candidates.confirmDialog.when)}
                </Typography>
                <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                  {new Date(
                    Number(selectedRow.interviewStartTime) * 1000,
                  ).toLocaleString([], {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
              </Box>
            </Paper>

            {/* Type */}
            <Paper
              variant='outlined'
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                bgcolor: 'action.hover',
              }}
            >
              <CircleCheckIcons size={24} color='#22c55e' />
              <Box>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  {t(SI.candidates.confirmDialog.type)}
                </Typography>
                <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                  {selectedRow.interviewType}
                </Typography>
              </Box>
            </Paper>

            {/* Link or Address */}
            <Paper
              variant='outlined'
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                bgcolor: 'action.hover',
              }}
            >
              {selectedRow.interviewType === 'Virtual' ? (
                <LinkIcons size={24} color='#3b82f6' />
              ) : (
                <MegaPhoneIcons size={24} color='#ef4444' />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  {selectedRow.interviewType === 'Virtual'
                    ? t(SI.candidates.confirmDialog.meetingLink)
                    : t(SI.candidates.confirmDialog.location)}
                </Typography>
                {selectedRow.interviewType === 'Virtual' ? (
                  <Link
                    href={selectedRow.meetingLink}
                    target='_blank'
                    rel='noopener noreferrer'
                    sx={{
                      wordBreak: 'break-all',
                      color: 'primary.main',
                      textDecoration: 'underline',
                      display: 'block',
                    }}
                  >
                    {selectedRow.meetingLink}
                  </Link>
                ) : (
                  <Typography variant='body2' sx={{ color: 'text.primary' }}>
                    {selectedRow.inPersonAddress}
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* Footer note */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircleCheckIcons size={20} color='#22c55e' />
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                {t(SI.candidates.confirmDialog.confirmNote)}
              </Typography>
            </Box>
          </Stack>
        )}
      </Dialog>

      <RecordInterviewDialog
        handleCloseRecordDialog={closeRecordClick}
        open={isRecordingDialogOpen}
        onRecordInterview={handleRecordingSave}
        onOpenPanelistNotes={onOpenPanelistNotes}
        candidate={selectedRow ?? ({} as ICandidate)}
      />
      <PanelistNotesDialog
        open={isPanelistNotesOpen}
        handleClosePanelistNotes={handleClosePanelistNotes}
        interviewRoundId={selectedRow?.interviewRoundId ?? ''}
        applicantId={selectedRow?.id ?? ''}
        email={selectedRow?.email ?? ''}
        intervieweeName={`${selectedRow?.firstName ?? ''} ${
          selectedRow?.lastName ?? ''
        }`}
        onSuccess={() => {}}
      />
    </>
  );
};

export default Candidates;
